import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  TablePagination,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Visibility, 
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { FiberManualRecord as DotIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import axios from 'axios';

const CustomerPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const customerResponse = await axios.get('https://apidashboard.eurekagroup.id/customers');
        const buResponse = await axios.get('https://apidashboard.eurekagroup.id/bu');

        const customersData = customerResponse.data;
        const buData = buResponse.data;

        const buMap = {};
        buData.forEach((bu) => {
          buMap[bu._id] = bu.bu_name;
        });

        const customersWithBuName = customersData.map((customer) => ({
          ...customer,
          bu_name: buMap[customer.id_bu] || 'N/A'
        }));
        setCustomers(customersWithBuName);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddCustomer = () => {
    navigate('/kontak/customer/add');
  };

  const handleEditCustomer = (customerId) => {
    navigate(`/kontak/customer/edit/${customerId}`);
  };

  const handleDelete = async (id) => {
    SwalConfig.deleteConfirm('customer ini').then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('token');
  
    try {
      await axios.delete(`https://apidashboard.eurekagroup.id/customers/${id}`, {
        headers: {
              Authorization: `Bearer ${token}`,
        },
      });
      setCustomers(customers.filter((customer) => customer._id !== id));
          
          SwalConfig.success(
            'Berhasil Dihapus!',
            'Data customer telah berhasil dihapus dari sistem.',
            2000
          );
    } catch (error) {
      console.error('Error deleting customer:', error);
          SwalConfig.error('Gagal menghapus customer', 'Terjadi kesalahan saat menghapus data');
        }
    }
    });
  };  

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleBusinessTypeFilterChange = (event) => {
    setBusinessTypeFilter(event.target.value);
  };

  const getStatusColor = (status) => {
    return status === 1 ? 'success' : 'error';
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.pic_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.mobile.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || 
                         (statusFilter === 'active' && customer.is_active === 1) ||
                         (statusFilter === 'inactive' && customer.is_active === 0);
    const matchesBusinessType = businessTypeFilter === '' || customer.business_type === businessTypeFilter;
    
    return matchesSearch && matchesStatus && matchesBusinessType;
  });

  const uniqueBusinessTypes = [...new Set(customers.map(customer => customer.business_type))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Customer
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data customer dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
              action={
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />} 
              onClick={handleAddCustomer}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Customer
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                  <TextField
                  fullWidth
                  label="Cari Customer"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Status</em>
                    </MenuItem>
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Tidak Aktif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={businessTypeFilter}
                    onChange={handleBusinessTypeFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Tipe Bisnis</em>
                    </MenuItem>
                    {uniqueBusinessTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredCustomers.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Customer
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
              {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
              ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        minWidth: 200
                      }}>
                        Customer
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        PIC
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kontak
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Tipe Bisnis
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Business Unit
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCustomers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((customer, index) => (
                      <TableRow 
                        key={customer._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                              <BusinessIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {customer.customer_name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {customer.billing_address || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {customer.pic_title} {customer.pic_last_name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {customer.pic_position}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {customer.mobile}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {customer.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.business_type}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {customer.bu_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                            color={getStatusColor(customer.is_active)}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => navigate(`/kontak/customer/view/${customer._id}`)} 
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { 
                                  bgcolor: 'success.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleEditCustomer(customer._id)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDelete(customer._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredCustomers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
              />
              </>
            )}
          </Box>
            </CardContent>
          </Card>
    </div>
  );
};

export default CustomerPage;
